<template>
    <BaseContainer>
        <span slot="principal">
            <v-content class="tela pr-2 pl-2">
                <v-toolbar class="ma-2" style="border-radius: 30px;">
                    <v-btn icon link to="/">
                        <v-icon>mdi-arrow-left</v-icon>
                    </v-btn>
                    <v-spacer/>
                    <v-toolbar-title v-show="$vuetify.breakpoint.smAndUp">Metas Financeiras</v-toolbar-title>
                    <v-spacer/>
                    <v-btn color="primary" fab small @click="openCadastro">
                        <v-icon>mdi-plus</v-icon>
                    </v-btn>
                </v-toolbar>

                <v-row class="ma-1" dense>
                    <v-col cols="12" md="7">
                        <v-card elevation="2" class="fill-height">
                            <v-card-title class="text-subtitle-1">
                                Previsto x Realizado
                                <v-spacer/>
                                <v-switch
                                    v-model="apenasVigentes"
                                    label="Apenas vigentes"
                                    hide-details
                                    dense
                                    @change="consultarComparativo"
                                />
                            </v-card-title>
                            <v-card-text>
                                <BarChart
                                    v-if="comparativo.length"
                                    :labels="comparativo.map(m => m.nome)"
                                    :datasets="[
                                        { label: 'Previsto', data: comparativo.map(m => m.previsto), color: 'rgba(25, 118, 210, 0.7)' },
                                        { label: 'Realizado', data: comparativo.map(m => m.realizado), color: 'rgba(76, 175, 80, 0.7)' },
                                    ]"
                                    :height="alturaGrafico"
                                />
                                <div v-else class="text-center grey--text pa-6">
                                    Nenhuma meta para exibir
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>

                    <v-col cols="12" md="5">
                        <v-card elevation="2" class="fill-height">
                            <v-card-title class="text-subtitle-1">
                                Metas cadastradas
                                <v-spacer/>
                                <span class="caption grey--text">{{ metas.length }} metas</span>
                            </v-card-title>
                            <v-card-text class="pa-2 lista-rolante">
                                <v-list three-line v-if="metas.length">
                                    <v-list-item
                                        v-for="m in metas"
                                        :key="m.id"
                                        @click="editar(m)"
                                    >
                                        <v-list-item-content>
                                            <v-list-item-title>{{ m.nome }}</v-list-item-title>
                                            <v-list-item-subtitle class="d-flex align-center">
                                                <span
                                                    class="ponto-categoria mr-2"
                                                    :style="{ backgroundColor: corCategoria(m) }"
                                                />
                                                {{ nomeCategoria(m) }}
                                            </v-list-item-subtitle>
                                            <v-list-item-subtitle>
                                                {{ m.conta && m.conta.nome }} ·
                                                {{ formatDate(m.data_inicio) }} → {{ formatDate(m.data_fim) }}
                                            </v-list-item-subtitle>
                                            <div class="mt-1">
                                                <v-progress-linear
                                                    :value="Math.min(m.percentual, 100)"
                                                    :color="progressColor(m.percentual)"
                                                    height="10"
                                                    rounded
                                                />
                                                <div class="d-flex justify-space-between caption mt-1">
                                                    <span>{{ formatCurrency(m.realizado) }} / {{ formatCurrency(m.total) }}</span>
                                                    <span>{{ m.percentual }}%</span>
                                                </div>
                                            </div>
                                        </v-list-item-content>
                                        <v-list-item-action>
                                            <v-btn icon small @click.stop="excluir(m)">
                                                <v-icon small color="error">mdi-delete</v-icon>
                                            </v-btn>
                                        </v-list-item-action>
                                    </v-list-item>
                                </v-list>
                                <div v-else class="text-center grey--text pa-4">
                                    Nenhuma meta cadastrada
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <MetaForm
                    :visible="visible"
                    :formData="form"
                    :contas="contas"
                    :categorias="categorias"
                    @onCadastro="onCadastro"
                    @close="close"
                />
            </v-content>
        </span>
    </BaseContainer>
</template>

<script>
import BaseContainer from '@/components/BaseContainer';
import BarChart from '@/components/charts/BarChart';
import MetaForm from './MetaForm';
import moment from 'moment';

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

export default {
    name: 'Metas',

    components: {
        BaseContainer,
        BarChart,
        MetaForm,
    },

    data: () => ({
        loading: false,
        visible: false,
        metas: [],
        comparativo: [],
        contas: [],
        categorias: [],
        form: {},
        apenasVigentes: false,
    }),

    computed: {
        alturaGrafico() {
            return this.$vuetify.breakpoint.mdAndUp ? 380 : 280;
        },
    },

    mounted() {
        this.carregarCadastros().then(() => {
            this.consultar();
            this.consultarComparativo();
        });
    },

    methods: {
        async carregarCadastros() {
            const [contasRes, catsRes] = await Promise.all([
                this.$http.get('financeiro-interno/contas'),
                this.$http.get('financeiro-interno/categorias'),
            ]);
            this.contas = (contasRes.data.data || []).filter(c => c.ativo);
            this.categorias = (catsRes.data.data || []).filter(c => c.ativo);
        },

        consultar() {
            this.loading = true;
            this.$http.get('financeiro-interno/metas')
                .then(resp => { this.metas = resp.data.data; })
                .catch(() => alert('Não foi possível carregar as metas'))
                .finally(() => (this.loading = false));
        },

        consultarComparativo() {
            const params = {};
            if (this.apenasVigentes) params.vigentes = 1;

            this.$http.get('financeiro-interno/metas/comparativo', { params })
                .then(resp => { this.comparativo = resp.data.data; })
                .catch(() => { this.comparativo = []; });
        },

        openCadastro() {
            this.form = {
                data_inicio: moment().startOf('month').format('YYYY-MM-DD'),
                data_fim: moment().endOf('month').format('YYYY-MM-DD'),
            };
            this.visible = true;
        },

        editar(m) {
            this.form = JSON.parse(JSON.stringify(m));
            this.visible = true;
        },

        close() {
            this.visible = false;
            this.form = {};
        },

        onCadastro() {
            this.close();
            this.consultar();
            this.consultarComparativo();
        },

        excluir(m) {
            if (!confirm(`Excluir a meta "${m.nome}"?`)) return;
            this.$http.delete(`financeiro-interno/metas/${m.id}`)
                .then(() => {
                    this.consultar();
                    this.consultarComparativo();
                })
                .catch(e => alert(e?.response?.data?.message || 'Erro ao excluir'));
        },

        nomeCategoria(m) {
            return m.categoria ? m.categoria.nome : 'Todas as categorias';
        },
        corCategoria(m) {
            return (m.categoria && m.categoria.cor) || '#9E9E9E';
        },
        formatCurrency(v) {
            return brl.format(Number(v) || 0);
        },
        formatDate(v) {
            return v ? moment(v).format('DD/MM/YYYY') : '';
        },
        progressColor(p) {
            if (p >= 100) return 'error';
            if (p >= 80) return 'warning';
            if (p >= 50) return 'primary';
            return 'success';
        },
    },
};
</script>

<style scoped>
.tela {
    min-height: 100vh;
}

.lista-rolante {
    max-height: 460px;
    overflow-y: auto;
}

.ponto-categoria {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex: 0 0 auto;
}
</style>
